// MobileSearch — tela de BUSCA da superfície MOBILE (touch). Parede A6.
//
// Input com teclado NATIVO (autofocus) + debounce + filtro por tipo (chips) +
// grid/lista de resultados. REUSA a MESMA busca da TV: api.search() chama o
// backend /catalog/search (pg_trgm + ranking do @app-iptv/search-core). SÓ a
// apresentação é touch (sem Norigin/foco de remoto). Lógica de filtro/itens/
// estados vive em mobile-search.ts (pura, testável sem DOM).
//
// Tap num resultado → onOpenItem (o shell roteia: VOD/série -> detalhe A5;
// canal -> player A4). no_provider (busca sem fonte) -> mesmo CTA das abas.

import { useTranslation } from '@app-iptv/i18n'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { api, ApiError } from '../api'
import { useProviderQueryKey } from '../hooks/useScopedQueryKey'
import { useSessionStore } from '../store'

import {
  buildResultItems,
  buildTypeChips,
  isSearchEnabled,
  kindForFilter,
  SEARCH_DEBOUNCE_MS,
  type SearchData,
  type SearchFilterType,
} from './mobile-search'
import { MobileGrid } from './MobileGrid'
import type { MobileHomeItem } from './MobileHome'
import styles from './MobileSearch.module.css'
import { MobileEmpty, MobileLoading, MobileNoSource } from './MobileStates'
import { MobileVodCard } from './MobileVodCard'

function isNoProvider(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'no_provider'
}

export interface MobileSearchProps {
  /** Tap num resultado — o shell roteia (canal -> player; VOD/série -> detalhe). */
  onOpenItem: (item: MobileHomeItem) => void
  /** Tap no CTA "configure fonte" (busca sem fonte ativa). */
  onConfigureSource?: (() => void) | undefined
}

export function MobileSearch({ onOpenItem, onConfigureSource }: MobileSearchProps): JSX.Element {
  const { t } = useTranslation()
  // Restauração da busca (fix/busca-estado-perdido). No mobile o MobileShell
  // desmonta a MobileSearch ao abrir o detalhe; o goBack volta pra busca
  // (goBackState) e esta tela REMONTA. Reidrata query/filtro do mesmo
  // useSessionStore da TV (solução única TV+mobile, provider-scoped via reset).
  // Seed lido 1x no mount (refs não mudam entre renders desta montagem).
  const lastSearchQuery = useSessionStore((s) => s.lastSearchQuery)
  const lastSearchFilterType = useSessionStore((s) => s.lastSearchFilterType)
  const setLastSearchQuery = useSessionStore((s) => s.setLastSearchQuery)
  const setLastSearchFilterType = useSessionStore((s) => s.setLastSearchFilterType)
  const seedRef = useRef({
    query: lastSearchQuery ?? '',
    filterType: (lastSearchFilterType ?? 'all') as SearchFilterType,
  })
  const [input, setInput] = useState(seedRef.current.query)
  const [debounced, setDebounced] = useState(seedRef.current.query)
  const [filterType, setFilterType] = useState<SearchFilterType>(seedRef.current.filterType)
  const inputRef = useRef<HTMLInputElement>(null)

  // Autofocus no input ao abrir a busca: o teclado nativo sobe na hora (o
  // usuário tocou na lupa pra buscar). Sem cleanup (foco é one-shot).
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Debounce input -> debounced (dispara a busca). Cleanup único = clearTimeout.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(input.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [input])

  // Persiste query/filtro no store pra sobreviver ao remount (volta do
  // detalhe). Termo vazio → null (não restaura busca fantasma). Mesma
  // semântica da SearchScreen da TV — store provider-scoped (anti-vazamento).
  useEffect(() => {
    setLastSearchQuery(debounced.length > 0 ? debounced : null)
  }, [debounced, setLastSearchQuery])
  useEffect(() => {
    setLastSearchFilterType(filterType)
  }, [filterType, setLastSearchFilterType])

  const enabled = isSearchEnabled(debounced)
  const kind = kindForFilter(filterType)

  const searchQ = useQuery({
    // Mesma key/forma da SearchScreen da TV (escopada por provider).
    queryKey: useProviderQueryKey(['mobile-search', debounced, kind]),
    queryFn: () => api.search(debounced, 30, undefined, kind),
    enabled,
    staleTime: 60_000,
    // keepPreviousData evita flicker ao trocar o chip de tipo.
    placeholderData: keepPreviousData,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })

  const data: SearchData | null = searchQ.data ?? null
  const noProvider = isNoProvider(searchQ.error)

  const chips = useMemo(() => (data ? buildTypeChips(data) : []), [data])
  const items = useMemo(
    () => (data ? buildResultItems(data, filterType) : []),
    [data, filterType],
  )

  const showLoading = enabled && searchQ.isLoading && !data
  const showEmpty = enabled && !!data && items.length === 0 && !searchQ.isFetching
  const showIdle = !enabled

  if (noProvider) {
    return (
      <div className={styles.screen} data-screen="search">
        {renderInput(t, input, setInput, inputRef)}
        <MobileNoSource onConfigureSource={onConfigureSource} />
      </div>
    )
  }

  return (
    <div className={styles.screen} data-screen="search">
      {renderInput(t, input, setInput, inputRef)}

      {/* Chips de tipo — só com busca ativa e resultados (espelha a TV). Sem o
          guard `enabled`, o keepPreviousData mantinha os chips no estado idle. */}
      {enabled && chips.length > 0 && (
        <div className={styles.typeChips} role="tablist" aria-label={t('nav.search')}>
          {chips.map((chip) => {
            const active = chip.type === filterType
            return (
              <button
                key={chip.type}
                type="button"
                role="tab"
                className={styles.typeChip}
                data-type={chip.type}
                data-active={active ? 'true' : 'false'}
                aria-selected={active ? 'true' : 'false'}
                onClick={() => setFilterType(chip.type)}
              >
                <span>{t(`search.${chip.type}`)}</span>
                <span className={styles.typeChipCount}>{chip.count}</span>
              </button>
            )
          })}
        </div>
      )}

      {showIdle && (
        <div className={styles.idle} data-state="idle">
          <p className={styles.idleText}>{t('search.startTyping')}</p>
        </div>
      )}

      {showLoading && <MobileLoading />}

      {showEmpty && (
        <MobileEmpty title={t('search.noResults')} text={t('empty.noResultsDesc')} />
      )}

      {enabled && items.length > 0 && (
        <MobileGrid gridId="search">
          {items.map((it) => (
            <MobileVodCard
              key={`${it.kind}-${it.id}`}
              title={it.title}
              poster={it.poster}
              year={it.year}
              kind={it.kind}
              onPress={() =>
                onOpenItem({ id: it.id, kind: it.kind, title: it.title, poster: it.poster })
              }
            />
          ))}
        </MobileGrid>
      )}
    </div>
  )
}

/** Input de busca nativo (autofocus via ref). Extraído pra reuso no estado
 *  no_provider (a barra continua visível). */
function renderInput(
  t: (k: string) => string,
  value: string,
  onChange: (v: string) => void,
  ref: React.RefObject<HTMLInputElement>,
): JSX.Element {
  return (
    <div className={styles.inputWrap}>
      <span className={styles.inputIcon} aria-hidden="true">
        ⌕
      </span>
      <input
        ref={ref}
        className={styles.input}
        type="search"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={t('search.placeholder')}
        aria-label={t('nav.search')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value.length > 0 && (
        <button
          type="button"
          className={styles.clear}
          aria-label={t('common.cancelAction')}
          onClick={() => onChange('')}
        >
          ×
        </button>
      )}
    </div>
  )
}
